/*!
 * clyro-shop.js - the ONE fixed purchase module for template-simple.
 *
 * This file is FIXED infrastructure. The chat agent never rewrites it; sections
 * only wire markup to the hooks below. Vanilla fetch + Alpine, no other deps.
 *
 * Alpine store `cart`:
 *   { items:[], item_count:0, total_price:0, open:false, loading:false, loadingKey:null }
 *
 * window.clyroShop public surface (sections + layout depend on these EXACT names):
 *   add(variantId, qty=1)        -> POST /cart/add.js    -> refresh() -> openCart()
 *   addMany([{id,quantity}])     -> POST /cart/add.js {items} -> one line per variant (packs)
 *   change(lineKey, qty)         -> POST /cart/change.js -> refresh()
 *   refresh()                    -> GET  /cart.js        -> updates Alpine.store('cart')
 *   openCart()                   -> Alpine.store('cart').open = true
 *   closeCart()                  -> Alpine.store('cart').open = false
 *   money(cents)                 -> Shopify-style price string
 *   variantFrom(optionValues, product) -> { id, price, available,
 *       compare_at_price, featured_image } | null
 *   buyBox(seed)                 -> Alpine x-data factory for the product
 *       buy-box (variant pick, live price, gallery sync, bundle-tier qty).
 *       seed = { qty, tier, sel: [option values], price: fallback cents }.
 *       The section supplies markup + <script type="application/json"
 *       x-ref="pdata">{"variants": [...]}</script>; behavior lives HERE.
 */
(function () {
  var BASE_CART = { items: [], item_count: 0, total_price: 0, open: false, loading: false, loadingKey: null };

  function store() {
    return (window.Alpine && window.Alpine.store) ? window.Alpine.store('cart') : null;
  }

  function applyCart(data) {
    var c = store();
    if (!c || !data) return;
    c.items = Array.isArray(data.items) ? data.items : [];
    c.item_count = typeof data.item_count === 'number' ? data.item_count : 0;
    c.total_price = typeof data.total_price === 'number' ? data.total_price : 0;
    // Socket contract (shared with the full base): transplanted headers carry
    // static [data-clyro-cart-count] badges — keep them live — and external
    // listeners (transfer smoke, integrations) hear 'cart:updated'.
    try {
      var badges = document.querySelectorAll('[data-clyro-cart-count]');
      for (var i = 0; i < badges.length; i++) badges[i].textContent = String(c.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (e) { /* never let socket sync break the cart */ }
  }

  function setLoading(on, key) {
    var c = store();
    if (!c) return;
    if (on) {
      c.loading = true;
      if (key != null) c.loadingKey = String(key);
    } else {
      c.loading = false;
      c.loadingKey = null;
    }
  }

  // ── money ────────────────────────────────────────────────────────
  // Shopify ships prices in cents. Use the live shop currency symbol if the
  // storefront exposed it, otherwise fall back to a plain "$" prefix.
  function money(cents) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    var amount = (n / 100).toFixed(2);
    var symbol = '$';
    try {
      var cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (cur === 'EUR') symbol = '€';
      else if (cur === 'GBP') symbol = '£';
      else if (cur === 'JPY') { symbol = '¥'; amount = String(Math.round(n / 100)); }
    } catch (_) {}
    return symbol + amount;
  }

  // ── variant resolution ───────────────────────────────────────────
  // optionValues = array of selected option strings, in option order.
  // product = a Shopify product JSON object (product.variants[]).
  function variantFrom(optionValues, product) {
    if (!product || !Array.isArray(product.variants)) return null;
    var opts = optionValues || [];
    var match = product.variants.find(function (v) {
      return (opts[0] == null || v.option1 === opts[0])
          && (opts[1] == null || v.option2 === opts[1])
          && (opts[2] == null || v.option3 === opts[2]);
    });
    if (!match) return null;
    return {
      id: match.id,
      price: match.price,
      available: !!match.available,
      compare_at_price: match.compare_at_price || 0,
      featured_image: match.featured_image || null
    };
  }

  // ── cart drawer toggles ──────────────────────────────────────────
  function openCart() {
    var c = store();
    if (c) c.open = true;
  }
  function closeCart() {
    var c = store();
    if (c) c.open = false;
  }

  // ── cart network ops (Shopify AJAX API) ──────────────────────────
  function refresh() {
    var c = store();
    if (c) c.loading = true;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) { applyCart(data); return data; })
      .catch(function (err) { console.error('[clyro-shop] refresh failed', err); })
      .finally(function () { setLoading(false); });
  }

  function add(variantId, qty) {
    var quantity = Math.max(1, Number(qty) || 1);
    var id = Number(variantId) || variantId;
    if (!id) {
      console.warn('[clyro-shop] add: no variant id');
      return Promise.resolve(null);
    }
    setLoading(true, 'add');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: id, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (item) { return refresh().then(function () { openCart(); return item; }); })
      .catch(function (err) { console.error('[clyro-shop] add failed', err); setLoading(false); });
  }

  /**
   * Add SEVERAL lines in one request (packs, bundles, "buy 2 get 2").
   *
   * A pack is not one line of quantity N: each item can be a DIFFERENT variant
   * (two wraps, one beige and one black), and Shopify models that as one line
   * per variant. /cart/add.js takes an `items` array for exactly this.
   *
   * IMPORTANT, and the theme cannot change it: Shopify charges what the lines
   * are worth. A pack that DISPLAYS "$48.90 instead of $112.00" is charged
   * $112.00 unless the shop carries a matching discount (an automatic
   * "Buy X get Y", a discount function, or a real pack product). The transfer
   * report says which one to create; this function only makes the cart right.
   */
  function addMany(lines) {
    var items = [];
    for (var i = 0; i < (lines || []).length; i++) {
      var l = lines[i] || {};
      var id = Number(l.id) || l.id;
      if (!id) continue;
      var qty = Math.max(1, Number(l.quantity) || 1);
      // Same variant twice with no distinguishing properties is ONE line of
      // quantity 2, not two lines - which is also what Shopify would collapse
      // it to, so we send what we mean.
      var merged = null;
      for (var j = 0; j < items.length; j++) {
        if (String(items[j].id) === String(id) && !items[j].properties && !l.properties) { merged = items[j]; break; }
      }
      if (merged) { merged.quantity += qty; continue; }
      var item = { id: id, quantity: qty };
      if (l.properties) item.properties = l.properties;
      items.push(item);
    }
    if (!items.length) {
      console.warn('[clyro-shop] addMany: no variant ids');
      return Promise.resolve(null);
    }
    if (items.length === 1 && !items[0].properties) return add(items[0].id, items[0].quantity);
    setLoading(true, 'add');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { return refresh().then(function () { openCart(); return data; }); })
      .catch(function (err) { console.error('[clyro-shop] addMany failed', err); setLoading(false); });
  }

  function change(lineKey, qty) {
    var quantity = Math.max(0, Number(qty) || 0);
    if (!lineKey) {
      console.warn('[clyro-shop] change: no line key');
      return Promise.resolve(null);
    }
    setLoading(true, lineKey);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { applyCart(data); return data; })
      .catch(function (err) { console.error('[clyro-shop] change failed', err); })
      .finally(function () { setLoading(false); });
  }

  // ── product buy-box (Alpine x-data factory) ──────────────────────
  // Aura-grade behaviors, fixed here so sections stay markup-only:
  // ?variant= URL sync (read at init, replaceState on pick), a
  // 'variant:change' event for other sections, sold-out option-value
  // detection, percent OR fixed tier discounts, per-unit tier pricing.
  function buyBox(seed) {
    seed = seed || {};
    var owner = null;
    function tierCents(q, price, amt, dtype) {
      if (dtype === 'fixed') return Math.max(0, Math.round(q * price - amt * 100));
      return Math.round(q * price * (100 - amt) / 100);
    }
    return {
      qty: seed.qty || 1,
      tier: (typeof seed.tier === 'number') ? seed.tier : -1,
      activeImg: 0,
      sel: seed.sel || [],
      cv: null,
      pdata: { variants: [], media_ids: [] },
      mediaIndex: function (v) {
        if (!v || !v.featured_image) return this.activeImg;
        var img = v.featured_image;
        if (typeof img.position === 'number' && img.position > 0) return img.position - 1;
        var ids = this.pdata.media_ids;
        if (Array.isArray(ids) && img.id != null) {
          var i = ids.findIndex(function (id) { return id === img.id || String(id) === String(img.id); });
          if (i >= 0) return i;
        }
        return this.activeImg;
      },
      syncImg: function (v) {
        var idx = this.mediaIndex(v);
        if (typeof idx === 'number' && idx >= 0) this.activeImg = idx;
      },
      init: function () {
        owner = this.$el || null;
        try {
          var data = JSON.parse(this.$refs.pdata.textContent);
          if (data && Array.isArray(data.variants)) this.pdata = { variants: data.variants, media_ids: Array.isArray(data.media_ids) ? data.media_ids : [] };
        } catch (_) {}
        var urlId = null;
        try { urlId = new URLSearchParams(window.location.search).get('variant'); } catch (_) {}
        var fromUrl = urlId && this.pdata.variants.find(function (v) { return String(v.id) === urlId; });
        if (fromUrl) {
          this.sel = [fromUrl.option1, fromUrl.option2, fromUrl.option3].filter(function (o) { return o != null; });
        }
        this.cv = variantFrom(this.sel, this.pdata);
        this.syncImg(this.cv);
        this.publishVariant();
      },
      publishVariant: function () {
        // Persist this owner's current selection for checkout scripts that
        // initialize after Alpine. Null is an explicit unavailable selection.
        if (owner) {
          owner.setAttribute('data-clyro-buybox', '');
          owner.__clyroVariant = this.cv;
          owner.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: this.cv } }));
        } else if (this.$dispatch) this.$dispatch('variant:change', { variant: this.cv });
      },
      pick: function (i, val) {
        this.sel[i] = val;
        var v = variantFrom(this.sel, this.pdata);
        this.cv = v;
        if (v) {
          this.syncImg(v);
        }
        try {
          var url = new URL(window.location);
          if (v) url.searchParams.set('variant', v.id);
          else url.searchParams.delete('variant');
          history.replaceState({}, '', url.toString());
        } catch (_) {}
        this.publishVariant();
      },
      optAvailable: function (i, val) {
        var probe = this.sel.slice();
        probe[i] = val;
        var v = variantFrom(probe, this.pdata);
        return !!(v && v.available);
      },
      price: function () { return this.cv ? this.cv.price : 0; },
      compareAt: function () { return (this.cv && this.cv.compare_at_price) || 0; },
      saving: function () { var c = this.compareAt(); return c > this.price() ? Math.round((1 - this.price() / c) * 100) : 0; },
      tierTotal: function (q, amt, dtype) { return money(tierCents(q, this.price(), amt || 0, dtype)); },
      tierUnit: function (q, amt, dtype) { return money(Math.round(tierCents(q, this.price(), amt || 0, dtype) / Math.max(1, q))); },
      available: function () { return !!(this.cv && this.cv.available); }
    };
  }

  window.clyroShop = {
    add: add,
    addMany: addMany,
    change: change,
    refresh: refresh,
    openCart: openCart,
    closeCart: closeCart,
    money: money,
    variantFrom: variantFrom,
    buyBox: buyBox
  };

  // Register the cart store and prime it on first load so the count is live.
  document.addEventListener('alpine:init', function () {
    window.Alpine.store('cart', Object.assign({}, BASE_CART));
    refresh();
  });

  // Socket contract (shared with the full base): any [data-clyro-cart-open]
  // trigger opens the drawer — transplanted Canvas headers keep working even
  // though their skin markup never knew clyroShop.
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-clyro-cart-open]') : null;
    if (!t) return;
    e.preventDefault();
    openCart();
  });
})();
